// src/components/ProspectForm.js
import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createProspect,
  getProspects,
  clearProspectError,
} from '../actions/prospectActions';

import MaterialTable from 'material-table';
import { toast } from 'react-toastify';

import {
  Box,
  Typography,
  Alert,
  Link,
  Chip,
  Avatar,
  Container,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Fade,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

// material-table icon mapping (v1/v2)
import AddBox from '@material-ui/icons/AddBox';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const initialForm = {
  name: '',
  phoneNo: '',
  role: 'fos',
  aadharNo: '',
  panNo: '',
  rejoining: 'no',
  photo: null,
  aadharDoc: null,
  panDoc: null,
};

const ImageDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '90vw',
    maxHeight: '90vh',
    margin: theme.spacing(2),
  },
}));

const PopupImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '70vh',
  objectFit: 'contain',
  borderRadius: 8,
});

const ViewButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(0.5, 1),
  fontSize: '0.75rem',
  textTransform: 'none',
}));

const ProspectForm = () => {
  const dispatch = useDispatch();

  // Try common reducer shapes:
  const slice = useSelector((s) => s.prospect || s.prospectReducer || {});
  const loading = !!slice.loading;
  const error = slice.error || null;
  const success = !!slice.success;

  // Normalize prospects list to an array
  const rows = Array.isArray(slice.prospects)
    ? slice.prospects
    : Array.isArray(slice?.prospects?.data)
      ? slice.prospects.data
      : Array.isArray(slice?.data)
        ? slice.data
        : Array.isArray(slice?.list)
          ? slice.list
          : [];

  // form (kept if you post prospects elsewhere)
  const [form, setForm] = useState(initialForm);

  // camera (kept – used by your create flow)
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // image dialog
  const [imagePopup, setImagePopup] = useState({
    open: false,
    imageUrl: '',
    title: '',
    prospectName: '',
  });

  useEffect(() => {
    dispatch(getProspects());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      setForm(initialForm);
      stopCamera();
      dispatch(getProspects());
      toast('Prospect saved successfully', { type: 'success', autoClose: 1500 });
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => dispatch(clearProspectError()), 4000);
      return () => clearTimeout(t);
    }
  }, [error, dispatch]);

  // camera helpers
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOpen(true);
    } catch {
      toast('Camera not accessible.', { type: 'error', autoClose: 1500 });
    }
  };
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      setForm((f) => ({ ...f, photo: file }));
      stopCamera();
    }, 'image/jpeg', 0.9);
  };

  // image dialog handlers
  const handleImageClick = (imageUrl, title, prospectName) => {
    if (!imageUrl) return;

    // Handle both S3 URLs and base64 data URLs
    const finalUrl = imageUrl.startsWith('data:') ? imageUrl : imageUrl;
    setImagePopup({ open: true, imageUrl: finalUrl, title, prospectName });
  };
  const handleCloseImagePopup = () =>
    setImagePopup({ open: false, imageUrl: '', title: '', prospectName: '' });

  // doc cell used by table columns
  const DocCell = ({ fileObj, title, prospectName }) => {
    // fileObj is now just a URL string
    const url = typeof fileObj === 'string' ? fileObj : fileObj?.url || fileObj?.dataUrl || null;

    if (!url) return <Typography variant="body2" color="text.secondary">—</Typography>;

    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <ViewButton
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
          onClick={() => handleImageClick(url, title, prospectName)}
        >
          View
        </ViewButton>
        {/* <Link href={url} target="_blank" rel="noreferrer" underline="hover" sx={{ fontSize: '0.75rem' }}>
          Download
        </Link> */}
      </Box>
    );
  };



  const getRoleChipColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'fos':
        return 'primary';
      case 'manager':
        return 'secondary';
      case 'admin':
        return 'error';
      default:
        return 'default';
    }
  };
  const getRejoiningChipColor = (rejoining) => (rejoining === 'yes' ? 'success' : 'default');

  const columns = [
    {
      title: 'Name',
      field: 'name',
      sorting: true,
      // render: (row) => (
      //   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      //     <Avatar
      //       src={row?.photo} // Now it's just a URL string
      //       alt={row?.name}
      //       sx={{ width: 28, height: 28, cursor: row?.photo ? 'pointer' : 'default' }}
      //       onClick={() =>
      //         row?.photo && handleImageClick(row.photo, 'Profile Photo', row.name)
      //       }
      //     >
      //       {row?.name?.charAt(0)?.toUpperCase()}
      //     </Avatar>
      //     <Typography variant="body2" fontWeight={600}>
      //       {row?.name || '—'}
      //     </Typography>
      //   </Box>
      // ),
    },

    { title: 'Phone', field: 'phoneNo', sorting: true },
    {
      title: 'Role',
      field: 'role',
      sorting: true,
      render: (row) => (
        <Chip
          label={row?.role?.toUpperCase() || '—'}
          color={getRoleChipColor(row?.role)}
          size="small"
          variant="outlined"
        />
      ),
    },
    { title: 'Aadhar No.', field: 'aadharNo', sorting: true },
    { title: 'PAN No.', field: 'panNo', sorting: true },
    {
      title: 'Rejoining',
      field: 'rejoining',
      sorting: true,
      render: (row) => (
        <Chip
          label={
            typeof row?.rejoining === 'string'
              ? row.rejoining.toUpperCase()
              : String(row?.rejoining ?? '—').toUpperCase()
          }
          color={getRejoiningChipColor(row?.rejoining)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      title: 'Photo',
      sorting: false,
      render: (row) => (
        <DocCell fileObj={row?.photo} title="Profile Photo" prospectName={row?.name} />
      ),
    },
    {
      title: 'Aadhar Doc',
      sorting: false,
      render: (row) => (
        <DocCell fileObj={row?.aadharDoc} title="Aadhar Document" prospectName={row?.name} />
      ),
    },
    {
      title: 'PAN Doc',
      sorting: false,
      render: (row) => (
        <DocCell fileObj={row?.panDoc} title="PAN Document" prospectName={row?.name} />
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
        Prospect Management
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Create and manage prospects with document uploads
      </Typography> */}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearProspectError())}>
          {String(error)}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" component="h3">Prospects List</Typography>
          </Box>

          <MaterialTable
            icons={tableIcons}
            title=""
            columns={columns}
            data={rows}
            isLoading={loading}
            options={{
              search: true,
              paging: true,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50],
              headerStyle: { position: 'sticky', top: 0, zIndex: 1 },
              sorting: true,
              selection: false, // set to true if you need bulk actions
              rowStyle: { height: 48, fontSize: 13 },
            }}
            onSearchChange={(txt) => {
              // If your API supports server-side search, call it here:
              // dispatch(getProspects(txt));
            }}
          />
        </CardContent>
      </Card>

      {/* Image Popup Dialog */}
      <ImageDialog
        open={imagePopup.open}
        onClose={handleCloseImagePopup}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        maxWidth="md"
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Box>
            <Typography variant="h6" component="div">{imagePopup.title}</Typography>
            {imagePopup.prospectName && (
              <Typography variant="body2" color="text.secondary">{imagePopup.prospectName}</Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseImagePopup} sx={{ color: 'grey.500' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
          {imagePopup.imageUrl && (
            <PopupImage
              src={imagePopup.imageUrl}
              alt={imagePopup.title}
              onError={(e) => { e.target.src = '/placeholder-image.png'; }}
            />
          )}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
            {/* <Button
              variant="outlined"
              onClick={() => {
                const link = document.createElement('a');
                link.href = imagePopup.imageUrl;
                link.download = `${imagePopup.prospectName}_${imagePopup.title}.jpg`;
                link.click();
              }}
            >
              Download Image
            </Button> */}
            <Button variant="contained" onClick={handleCloseImagePopup}>Close</Button>
          </Box>
        </DialogContent>
      </ImageDialog>

      {/* Hidden canvas for camera capture (if you use the capture feature elsewhere) */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <video ref={videoRef} style={{ display: 'none' }} />
    </Container>
  );
};

export default ProspectForm;
